/**
 * Unified comments composer — pick whether you're messaging the **HOD bench**
 * (All HODs / HOD Sales / HOD Marketing) or one **specific agent**, then send
 * a text comment, a recorded voice note, or an uploaded audio file. The
 * agent path lands in the recipient's "Feedback from Management" card; the
 * HOD path lands in the existing CEO↔HOD thread.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import VoiceMessageRecorder from './VoiceMessageRecorder';
import HelpInfo from './HelpInfo';
import { MessageSquare, Send, RefreshCw, Upload, UserRound, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HOD_TARGETS = [
  { value: 'all', label: 'All HODs' },
  { value: 'hod_sales', label: 'HOD Sales' },
  { value: 'hod_marketing', label: 'HOD Marketing' },
];

const CommentsToAgentPanel = () => {
  const { token, user } = useAuth();
  // The same composer is rendered on the CEO Dashboard and the HOD
  // Dashboard. The header copy used to hardcode "CEO ↔ HOD", which
  // read wrong when a Sales or Marketing HOD opened this panel to
  // message a peer HOD. Flip the label by the signed-in user's role.
  const senderLabel = (user?.role || '').startsWith('hod') ? 'HOD' : 'CEO';
  // recipientType: 'hod' (HOD bench), 'agent' (single agent — name required),
  // 'all_agents' (fan-out to every agent in the roster).
  const [recipientType, setRecipientType] = useState('agent');
  const [agents, setAgents] = useState([]);
  // Individual HODs the CEO can target by name. Fetched on demand
  // when the recipient toggle flips to "HODs" so the dropdown shows
  // the three group options + every HOD in the org.
  const [hods, setHods] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [targetHod, setTargetHod] = useState('all');
  const [priority, setPriority] = useState('normal');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Pull the HOD roster when this composer flips into HOD mode. The
  // endpoint is CEO-only, so a 403 here just means the current user
  // isn't a CEO — we silently leave `hods` empty and the dropdown
  // falls back to only showing the three group keywords.
  useEffect(() => {
    if (!token) return;
    if (recipientType !== 'hod') return;
    let cancelled = false;
    (async () => {
      try {
        // /messageable-hods (not /hods — that path is taken by the CEO
        // HODs-table endpoint). Returns {id, full_name, email, role}.
        const res = await fetch(`${API}/api/ceo/messageable-hods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setHods(data?.data?.hods || []);
      } catch (e) {
        // non-fatal — keep the group keywords-only fallback
      }
    })();
    return () => { cancelled = true; };
  }, [token, recipientType]);

  useEffect(() => {
    if (!token) return;
    if (recipientType === 'hod') return;
    const load = async () => {
      try {
        let list = [];
        // Primary: dedicated endpoint that returns every agent-role user,
        // including the modern role names (sales_agent / marketing_agent / cr_agent).
        const res = await fetch(`${API}/api/hod/messageable-agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          list = data?.data?.agents || [];
        }
        // Fallback to the older agent-status feed if the new endpoint isn't
        // present yet (older backend) — same shape, narrower role filter.
        if (!list.length) {
          const res2 = await fetch(`${API}/api/hod/agent-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res2.ok) {
            const data2 = await res2.json();
            list = data2?.data?.agents || [];
          }
        }
        setAgents(list);
      } catch (e) {
        // non-fatal
      }
    };
    load();
  }, [token, recipientType]);

  // True only when the selected recipient is concrete enough to send to.
  // 'all_agents' is concrete as soon as the roster has loaded; 'agent'
  // requires picking a specific name; 'hod' always has a target_hod.
  const hasRecipient = recipientType === 'hod'
    ? !!targetHod
    : recipientType === 'all_agents'
      ? agents.length > 0
      : !!agentId;
  // Visual cue for the mandatory single-agent picker.
  const agentRequiredMissing = recipientType === 'agent' && !agentId;

  const sendText = async () => {
    if (!hasRecipient) {
      toast.error(recipientType === 'agent' ? 'Select an agent name' : 'Pick a recipient first');
      return;
    }
    if (!text.trim()) { toast.error('Write something to send'); return; }
    setSending(true);
    try {
      if (recipientType === 'hod') {
        const res = await fetch(`${API}/api/ceo/comments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ comment_text: text, target_hod: targetHod, priority }),
        });
        const data = await res.json();
        if (!data.success) { toast.error(data.error || data.detail || 'Failed to send'); return; }
        toast.success('Message sent');
      } else if (recipientType === 'all_agents') {
        // Client-side fan-out — one direct-message per agent. Run in parallel
        // and report the count so the user knows the broadcast went through.
        const results = await Promise.allSettled(agents.map((a) => {
          const id = a.id || a.agent_id;
          return fetch(`${API}/api/hod/agents/${id}/direct-message`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ feedback_text: text, agent_id: id }),
          }).then((r) => r.json());
        }));
        const ok = results.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        toast.success(`Broadcast sent to ${ok} of ${agents.length} agents`);
      } else {
        const res = await fetch(`${API}/api/hod/agents/${agentId}/direct-message`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ feedback_text: text, agent_id: agentId }),
        });
        const data = await res.json();
        if (!data.success) { toast.error(data.error || data.detail || 'Failed to send'); return; }
        toast.success('Message sent');
      }
      setText('');
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  const onFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!hasRecipient) {
      toast.error(recipientType === 'agent' ? 'Select an agent name' : 'Pick a recipient first');
      return;
    }
    if (!file.type.startsWith('audio/')) { toast.error('Pick an audio file'); return; }
    setUploadingFile(true);
    try {
      const duration = await new Promise((resolve) => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve(Math.round(audio.duration) || 0);
        audio.onerror = () => resolve(0);
        audio.src = URL.createObjectURL(file);
      });

      const post = async (extras) => {
        const formData = new FormData();
        formData.append('audio', file, file.name);
        formData.append('duration_seconds', String(duration));
        Object.entries(extras).forEach(([k, v]) => formData.append(k, v));
        const res = await fetch(`${API}/api/voice-messages/upload?token=${encodeURIComponent(token)}`, {
          method: 'POST',
          body: formData,
        });
        return res.json();
      };

      if (recipientType === 'hod') {
        const data = await post({ chat_type: 'hod_comments', target_hod: targetHod });
        if (data.success) toast.success('Voice file sent');
        else toast.error(data.error || 'Upload failed');
      } else if (recipientType === 'all_agents') {
        const results = await Promise.allSettled(agents.map((a) =>
          post({ chat_type: 'agent_feedback', contact_id: a.id || a.agent_id })
        ));
        const ok = results.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        toast.success(`Voice file broadcast to ${ok} of ${agents.length} agents`);
      } else {
        const data = await post({ chat_type: 'agent_feedback', contact_id: agentId });
        if (data.success) toast.success('Voice file sent');
        else toast.error(data.error || 'Upload failed');
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.message || e}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const cardTitle = recipientType === 'hod'
    ? 'Comments to HOD'
    : recipientType === 'all_agents'
      ? 'Broadcast to All Agents'
      : 'Comments to Agent';
  const cardDescription = recipientType === 'hod'
    ? `Private channel to the HOD bench (${senderLabel} ↔ HOD)`
    : recipientType === 'all_agents'
      ? 'Send the same message or voice note to every agent on the team'
      : 'Send a private message or voice note to one specific agent';

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <MessageSquare size={18} className="text-violet-500" />
          {cardTitle}
          <HelpInfo
            align="inline"
            title="How to use this composer"
            steps={[
              <>Pick a recipient type from the <em>Send to</em> dropdown — <em>HODs</em>, <em>All Agents</em>, or a single <em>Agent</em>.</>,
              <>For HOD mode the second dropdown lists group keywords (<em>All HODs</em>, <em>HOD Sales</em>, <em>HOD Marketing</em>) and every HOD by name. Pick a name to send a private 1:1 instead of broadcasting.</>,
              'Type your text in the box, or use the mic to record a voice note, or click Upload audio to attach a pre-recorded clip.',
              <>Set Priority to <em>Urgent</em> when you need the recipient to see a red badge and a louder bell — keep it for time-sensitive items so the signal stays trustworthy.</>,
              'Replies come back into the same thread, so you can have a back-and-forth without leaving the dashboard.',
            ]}
            tip="When sending to one HOD by name, only that HOD sees it — the other HOD won't even know the message exists. Use this for performance feedback or sensitive direction."
          />
        </CardTitle>
        <CardDescription>{cardDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Recipient-type toggle + recipient picker */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500">Send to</span>
          <Select value={recipientType} onValueChange={(v) => { setRecipientType(v); setAgentId(''); }}>
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hod">
                <div className="flex items-center gap-1.5"><Users size={14} /> HODs</div>
              </SelectItem>
              <SelectItem value="all_agents">
                <div className="flex items-center gap-1.5"><Users size={14} /> All Agents</div>
              </SelectItem>
              <SelectItem value="agent">
                <div className="flex items-center gap-1.5"><UserRound size={14} /> Agent</div>
              </SelectItem>
            </SelectContent>
          </Select>

          {recipientType === 'hod' ? (
            <>
              <Select value={targetHod} onValueChange={setTargetHod}>
                <SelectTrigger className="h-9 w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Group-level keywords first — these still fan
                      out to multiple HOD recipients on the server. */}
                  {HOD_TARGETS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                  {/* Individual HOD names, alphabetised by the
                      backend. Picking one sends the comment only to
                      that HOD — useful for direct 1:1 instructions
                      that the other HOD doesn't need to see. */}
                  {hods.length > 0 && (
                    <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Specific HOD
                    </div>
                  )}
                  {hods.map((h) => {
                    const roleLabel = h.role === 'hod_sales'
                      ? 'Sales'
                      : h.role === 'hod_marketing'
                        ? 'Marketing'
                        : 'HOD';
                    return (
                      <SelectItem key={h.id} value={h.id}>
                        {(h.full_name || h.email || 'HOD')}
                        <span className="ml-1 text-[10px] text-slate-400">· {roleLabel}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="h-9 w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </>
          ) : recipientType === 'all_agents' ? (
            <span className="text-xs text-slate-600 px-2 py-1 rounded bg-slate-100 border border-slate-200">
              {agents.length} agent{agents.length === 1 ? '' : 's'} will receive this
            </span>
          ) : (
            <div className="flex items-center gap-1.5">
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger
                  className={`h-9 w-[260px] ${agentRequiredMissing ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                >
                  <SelectValue placeholder="Select an agent (required)" />
                </SelectTrigger>
                <SelectContent>
                  {agents.length === 0 && (
                    <SelectItem value="__none__" disabled>No agents found</SelectItem>
                  )}
                  {agents.map((a) => (
                    <SelectItem key={a.id || a.agent_id} value={a.id || a.agent_id}>
                      {a.full_name || a.agent_name || a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-red-500 text-xs font-semibold" title="Required">*</span>
            </div>
          )}
        </div>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            recipientType === 'hod'
              ? 'Write a comment or instruction for HOD…'
              : 'Write a private comment for this agent…'
          }
          rows={3}
        />

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={sendText} disabled={sending || !hasRecipient}>
            {sending ? <RefreshCw size={16} className="animate-spin mr-1" /> : <Send size={16} className="mr-1" />}
            Send {recipientType === 'hod' ? 'comment to HOD' : 'comment'}
          </Button>

          {recipientType === 'hod' ? (
            <VoiceMessageRecorder
              chatType="hod_comments"
              targetHod={targetHod}
              compact
              onSend={() => toast.success('Voice note sent')}
            />
          ) : recipientType === 'all_agents' ? (
            // Live recorder can only attach to one contact. For true broadcast
            // use the Upload audio path which fans out per agent.
            <VoiceMessageRecorder
              chatType="agent_feedback"
              contactId={(agents[0]?.id || agents[0]?.agent_id) || null}
              disabled={agents.length === 0}
              disabledReason="No agents loaded — wait a moment, then try again"
              compact
              onSend={() => toast('Voice note sent (single recipient — use Upload audio to broadcast)')}
            />
          ) : (
            <VoiceMessageRecorder
              chatType="agent_feedback"
              contactId={agentId || null}
              disabled={!agentId}
              disabledReason="Select an agent name first"
              compact
              onSend={() => toast.success('Voice note sent')}
            />
          )}

          <label className="inline-flex">
            <input
              type="file"
              accept="audio/*"
              hidden
              disabled={!hasRecipient || uploadingFile}
              onChange={onFileUpload}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasRecipient || uploadingFile}
              onClick={(e) => e.currentTarget.previousSibling.click()}
            >
              {uploadingFile ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
              Upload audio
            </Button>
          </label>
        </div>

        {agentRequiredMissing && (
          <p className="text-xs text-red-600">
            Selecting <strong>Agent</strong> requires a specific name — pick one to enable Send.
          </p>
        )}
        {recipientType === 'hod' && !targetHod && (
          <p className="text-xs text-amber-600">Pick a recipient first.</p>
        )}
        {recipientType === 'all_agents' && agents.length === 0 && (
          <p className="text-xs text-amber-600">
            No agents loaded yet — wait a moment for the roster to load.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CommentsToAgentPanel;
